import { Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
  MicrosoftDialPlanExceptionGroup,
  PrivilegedTenant,
  RangeType,
} from '../../../../shared/models/generated/ziroModels';
import { BreadcrumbsService } from '../../../../shared/breadcrumbs/breadcrumbs.service';
import { ButtonStyles } from '../../../../button/button.component';
import { ZiroIcons } from '../../../../shared/models/ziro-icons.enum';
import { ZiroModalService } from '../../../../shared/services/ziro-modal.service';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from '../../../../shared/services/toast.service';
import { ActivatedRoute, Router } from '@angular/router';
import {
  BottomNavService,
  BottomNavUpdateButtonsList,
  BottomNavUpdateState,
} from '../../../../shared/bottom-nav/bottom-nav.service';
import { Observable, Subscriber, Subscription, zip } from 'rxjs';
import { MicrosoftDialPlanExceptionManagementEditFormComponent } from './dial-plan-exception-management-edit-form/dial-plan-exception-management-edit-form.component';
import { ZiroFormsUpdate, ZiroFormsValidationState } from '../../../../forms/ziro-forms-models';
import { isEqual } from 'lodash';
import { TenantNavigationService } from '../../../services/tenant-navigation.service';
import { MsDialPlanExceptionGroupsContext } from '../../../contexts/ms-dial-plan-exception-groups.context';
import { ZiroFormStateService } from '../../../../forms/ziro-form-state.service';

@Component({
  selector: 'ziro-microsoft-dial-plan-exception-management-edit',
  templateUrl: './dial-plan-exception-management-edit.component.html',
  styleUrls: ['../../../admin-page.scss'],
  providers: [TenantNavigationService],
  standalone: false,
})
export class MicrosoftDialPlanExceptionManagementEditComponent implements OnInit, OnDestroy {
  private _breadcrumbsService = inject(BreadcrumbsService);
  private _ziroModalService = inject(ZiroModalService);
  private _translateService = inject(TranslateService);
  private _toastService = inject(ToastService);
  private _activatedRoute = inject(ActivatedRoute);
  private _bottomNavService = inject(BottomNavService);
  private _router = inject(Router);
  private _msDialPlanExceptionGroupsContext = inject(MsDialPlanExceptionGroupsContext);
  private _tenantNavigationService = inject(TenantNavigationService);
  private _ziroFormStateService = inject(ZiroFormStateService);

  @ViewChild(MicrosoftDialPlanExceptionManagementEditFormComponent)
  childFormComponent: MicrosoftDialPlanExceptionManagementEditFormComponent;

  buttonStyles = ButtonStyles;
  ziroIcons = ZiroIcons;
  isLoading = true;
  exceptionGroup = {
    name: '',
    exceptionRanges: [{ start: '', end: '' }],
    rangeType: RangeType.DID,
  } as MicrosoftDialPlanExceptionGroup;
  exceptionGroups: MicrosoftDialPlanExceptionGroup[];
  groupId: number;

  private _isValidFromDynamicValidator = ZiroFormsValidationState.VALID;
  private _subscriptions = new Subscription();
  private _tenant: PrivilegedTenant;
  private _tenantId: string;

  ngOnInit(): void {
    this.groupId = parseInt(this._activatedRoute.snapshot.params.groupId) || null;
    this._tenantId = this._activatedRoute.snapshot.params.tenantId;
    const sub = zip(
      this._msDialPlanExceptionGroupsContext.state$,
      this._msDialPlanExceptionGroupsContext.getDynamicNonTeamsDialPlanExceptionGroups(this._tenantId),
      this._tenantNavigationService.getTenantInfo(this._activatedRoute),
    ).subscribe(([staticGroups, dynamicGroups, tenantInfo]) => {
      this._tenant = tenantInfo;
      this.exceptionGroups = staticGroups.concat(dynamicGroups);
      if (this.groupId) {
        this.exceptionGroup = this.exceptionGroups.find(
          (g: MicrosoftDialPlanExceptionGroup) => Number(g.id) === this.groupId,
        );
      }
      this._setBreadcrumb(this.exceptionGroup?.name);
      this._initBottomNav();
      this.isLoading = false;
    });
    this._subscriptions.add(sub);
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
    this._bottomNavService.dispatch(
      new BottomNavUpdateState({
        hasValidationError: false,
      }),
    );
  }

  onFormUpdate(data: ZiroFormsUpdate<MicrosoftDialPlanExceptionGroup>): void {
    if (!isEqual(data.new, data.old)) {
      this.exceptionGroup = data.new;

      if (!isEqual(data.new.name, data.old?.name)) {
        this._setBreadcrumb(data.new.name);
      }
    }
  }

  onValidationFromDynamicValidator(validationState: ZiroFormsValidationState): void {
    this._isValidFromDynamicValidator = validationState;
  }

  private _initBottomNav(): void {
    const buttons = [
      {
        id: 'bottom-nav-cancel-button',
        dataAutomation: 'bottom-nav-cancel-button',
        label: 'tkey;global.button.cancel.text',
        buttonClass: this.buttonStyles.DEFAULT,
        cb: () => {
          this._router.navigate([this.exceptionGroup?.id ? '../..' : '../'], { relativeTo: this._activatedRoute });
        },
      },
      {
        id: 'bottom-nav-save-button',
        dataAutomation: 'bottom-nav-save-button',
        label: 'tkey;global.button.save.text',
        icon: this.ziroIcons.SAVE_SUCCESS,
        buttonClass: this.buttonStyles.PRIMARY,
        cb: () => this._onSaveClick(),
      },
    ];

    if (this.exceptionGroup?.id) {
      buttons.splice(1, 0, {
        id: 'bottom-nav-delete-button',
        dataAutomation: 'bottom-nav-delete-button',
        label: 'tkey;global.button.delete.text',
        icon: ZiroIcons.DELETE_SMALL,
        buttonClass: ButtonStyles.DANGER,
        cb: () => {
          this._deleteClicked();
        },
      });
    }

    this._bottomNavService.dispatch(new BottomNavUpdateButtonsList([...buttons]));
  }

  private _setBreadcrumb(label: string): void {
    this._breadcrumbsService.updateBreadcrumbs([
      {
        label: 'tkey;admin.tenants.title',
        routerLink: false,
      },
      {
        label: this._tenant?.tenantName,
        url: `/admin/tenants/${this._tenant?.tenantId}/app-registration`,
        routerLink: true,
      },
      {
        label: 'tkey;admin.ms_dial_plan_exception.title',
        url: `/admin/tenants/${this._tenant?.tenantId}/dial-plan-exception-management`,
        routerLink: true,
      },
      { label: label },
    ]);
  }

  private _canSubmitForms(): boolean {
    if (this.childFormComponent) {
      this.childFormComponent._validateAndSubmitSource.next(true);
      if (this.childFormComponent.extensionRangesDisplayForm) {
        this.childFormComponent.extensionRangesDisplayForm._validateAndSubmitSource.next(true);
        return true;
      }
    }
    return false;
  }

  private _isChildFormsValid(): boolean {
    const isChildFormValid = !!this.childFormComponent.isFormValid();
    const isChildFormRangeValid =
      !!this.childFormComponent.extensionRangesDisplayForm.isFormValid() &&
      this._isValidFromDynamicValidator === ZiroFormsValidationState.VALID;
    return isChildFormValid && isChildFormRangeValid;
  }

  private _onSaveClick(): void {
    this._bottomNavService.setBottomNavValidationError(false);

    const canSubmitForms = this._canSubmitForms();
    const isChildFormsValid = this._isChildFormsValid();

    if (!canSubmitForms || !isChildFormsValid) {
      this._bottomNavService.setBottomNavValidationError(true);
      return;
    }

    this._bottomNavService.setButtonPendingState('bottom-nav-save-button', true);
    this._bottomNavService.setButtonDisabledState('bottom-nav-cancel-button', true);
    this._bottomNavService.setButtonDisabledState('bottom-nav-delete-button', true);

    if (this.exceptionGroup.id) {
      this._msDialPlanExceptionGroupsContext.put(this.exceptionGroup).subscribe(() => this._afterSave());
    } else {
      this._msDialPlanExceptionGroupsContext.post(this.exceptionGroup).subscribe(() => this._afterSave());
    }
  }

  private _afterSave(): void {
    this._toastService.pushSaveToast(
      'tkey;admin.ms_dial_plan_exception.toast.title',
      this.exceptionGroup.name,
      this.ziroIcons.SAVE_SUCCESS,
    );
    this._router.navigate([this.exceptionGroup?.id ? '../../' : '../'], { relativeTo: this._activatedRoute });
  }

  private _deleteClicked(): void {
    const options = {
      windowClass: 'delete-button-modal',
      modalViewProperties: {
        icon: ZiroIcons.DELETE_BIG,
        iconClass: 'text-danger',
        title: this._translateService.instant('tkey;admin.msdialplan.management.delete_modal.title'),
        promptBody: this._translateService.instant('tkey;admin.ms_dial_plan_exception.delete.message', {
          name: this.exceptionGroup.name,
        }),
        displayCloseButton: true,
        buttons: [
          {
            label: 'tkey;dialogs.button.cancel',
            buttonClass: ButtonStyles.OUTLINE_DEFAULT,
            dataAutomation: 'confirmation-modal-cancel-button',
          },
          {
            label: 'tkey;dialogs.button.delete',
            buttonClass: ButtonStyles.OUTLINE_DANGER,
            dataAutomation: 'confirmation-modal-confirm-button',
            cb: () => {
              return this._deleteGroup(this.exceptionGroup);
            },
          },
        ],
      },
    };

    this._ziroModalService.openPromptModal(() => options.modalViewProperties, options);
  }

  private _deleteGroup(group: MicrosoftDialPlanExceptionGroup): Observable<void> {
    return new Observable((subscriber: Subscriber<void>) => {
      this._msDialPlanExceptionGroupsContext.delete(Number(group.id), group.name).subscribe(() => {
        this._ziroFormStateService.setIsFormDirty(false);
        this._router.navigate([this.exceptionGroup?.id ? '../..' : '../'], { relativeTo: this._activatedRoute });
        subscriber.next(null);
        subscriber.complete();
      });
    });
  }
}
